import { readStored, writeStored } from "./storage";
import type { SnipcatAttribution } from "./types";

/**
 * Ad attribution captured from the landing URL and remembered for the rest of
 * the visit.
 *
 * It is stored SEPARATELY from the session because the two have different
 * lifetimes: the visitor may land on the ad URL, browse away, come back
 * through a bookmark and only then convert. The click that paid for the visit
 * is the first one, so a later page without parameters must not erase it -
 * only a NEW non-empty parameter overwrites its own key (last touch per key,
 * first touch overall for keys nobody sends again).
 */

/** url query parameter -> stored key -> max length matching the server column. */
const PARAMS: Array<[string, keyof SnipcatAttribution, number]> = [
  ["utm_source", "utmSource", 255],
  ["utm_campaign", "utmCampaign", 255],
  ["utm_medium", "utmMedium", 255],
  ["utm_content", "utmContent", 255],
  ["fbclid", "fbclid", 255],
  ["gclid", "gclid", 255],
  ["campaign_id", "campaignId", 64],
  ["adset_id", "adsetId", 64],
  ["ad_id", "adId", 64],
];

/**
 * Aliases accepted for the same slot. Meta's dynamic URL parameters and the
 * hand-written links marketing people actually paste differ in spelling, and a
 * campaign lost to an underscore is invisible until the month is over.
 */
const ALIASES: Record<string, keyof SnipcatAttribution> = {
  utmsource: "utmSource",
  utmcampaign: "utmCampaign",
  utmmedium: "utmMedium",
  utmcontent: "utmContent",
  campaignid: "campaignId",
  adsetid: "adsetId",
  adid: "adId",
  fbclid: "fbclid",
  gclid: "gclid",
};

const limit = (value: string, maxLength: number): string =>
  value.length > maxLength ? value.slice(0, maxLength) : value;

const storageKey = (prefix: string, key: keyof SnipcatAttribution): string =>
  prefix + "attr_" + key;

/**
 * Reads the ad parameters off the current URL, persists the ones that are
 * present and returns everything known about this visit.
 */
export const captureAttribution = (prefix: string): SnipcatAttribution => {
  const search =
    typeof window === "undefined" ? "" : window.location.search || "";
  const params = new URLSearchParams(search);

  const write = (key: keyof SnipcatAttribution, raw: string | null): void => {
    const value = (raw || "").trim();
    if (!value) {
      return;
    }
    const max = PARAMS.find((item) => item[1] === key)?.[2] ?? 255;
    writeStored(storageKey(prefix, key), limit(value, max));
  };

  PARAMS.forEach(([urlParam, key]) => write(key, params.get(urlParam)));
  // second pass over the aliases, so `utmSource=` works as well as `utm_source=`
  params.forEach((value, name) => {
    const alias = ALIASES[name.toLowerCase().replace(/[-_]/g, "")];
    if (alias) {
      write(alias, value);
    }
  });

  return loadAttribution(prefix);
};

/** Everything remembered about where this visit came from. */
export const loadAttribution = (prefix: string): SnipcatAttribution => {
  const attribution: SnipcatAttribution = {};
  PARAMS.forEach(([, key]) => {
    const value = readStored(storageKey(prefix, key));
    if (value) {
      attribution[key] = value;
    }
  });
  return attribution;
};

/** True when nothing at all is known - used to skip empty request payloads. */
export const isAttributionEmpty = (attribution: SnipcatAttribution): boolean =>
  Object.keys(attribution).length === 0;
